import type { TaxJobDetailsViewModel } from '@/features/jobs/details/tax-type/TaxJobDetailsViewModel'
import { useLoaderData, useNavigate, useParams } from 'react-router'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { observer } from 'mobx-react-lite'
import { TaxInfoGatheringDialogsContainer } from '@/features/jobs/details/tax-type/info-gathering/components/TaxInfoGatheringDialogsContainer'
import { useActivation } from '@/hooks/view-model-hooks'
import { type ReactNode } from 'react'
import { links } from '@/utils/link'
import { useUrlSyncedViewModelState } from '@/hooks/router-hooks'
import {
  Wizard,
  WizardBottomBar,
  WizardBottomBarLabel,
  WizardBottomBarSection,
  WizardBottomBarSpacer,
  WizardContentHeader,
  WizardContentHeaderDescription,
  WizardContentHeaderKicker,
  WizardContentHeaderTitle,
  WizardPageLayout,
  WizardPageMainColumn,
  WizardPageAnimatedContent,
  WizardPageStickyColumn,
  WizardTopBar,
  WizardTopBarLogo,
  useWizardBottomBarPrimaryButton,
  WizardBottomBarPrimaryButton,
  WizardBottomBarPrimaryButtonContextProvider,
} from '@/components/Wizard'
import {
  useDisableGlobalNavigationIndicator,
  useHideGlobalNavbar,
} from '@/features/shell/global-navigation/GlobalNavigationContainer'
import { useRootStore } from '@/contexts/root-store-context'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { ButtonGroup } from '@/components/ButtonGroup'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import {
  TaxInfoGatheringMissingRequiredItemsModalContainer,
  TaxInfoGatheringNavContainer,
  TaxInfoGatheringNavigationModalContainer,
} from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGathering/TaxInfoGatheringNavContainer'
import {
  type SupportingItemViewModel,
  type TaxInfoGatheringViewModel,
} from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGatheringViewModel'
import { PersonalInfoWizardContentContainer } from '@/features/jobs/details/tax-type/info-gathering/personal-info/PersonalInfoWizardContentContainer'
import { SpousalInfoWizardContentContainer } from '@/features/jobs/details/tax-type/info-gathering/spousal-info/SpousalInfoWizardContentContainer'
import { DependentsInfoWizardContentContainer } from '@/features/jobs/details/tax-type/info-gathering/dependents-info/DependentsInfoWizardContentContainer'
import { PersonalTaxRefundMethodWizardContentContainer } from '@/features/jobs/details/tax-type/info-gathering/refund-methods/components/PersonalTaxRefundMethodWizardContentContainer'
import { BusinessTaxRefundMethodWizardContentContainer } from '@/features/jobs/details/tax-type/info-gathering/refund-methods/components/BusinessTaxRefundMethodWizardContentContainer'
import { DocumentWizardContentContainer } from '@/features/jobs/details/tax-type/info-gathering/documents/components/DocumentWizardContentContainer'
import { BusinessInfoWizardContentContainer } from '@/features/jobs/details/tax-type/info-gathering/business-info/components/BusinessInfoWizardContentContainer'
import { NotesWizardContentContainer } from '@/features/jobs/details/tax-type/info-gathering/notes/components/NotesWizardContentContainer'
import { TaxJobConversationContainer } from '@/features/jobs/details/tax-type/components/TaxJobConversationContainer'
import { CompleteInfoGatheringEarlyConfirmationDialogContainer } from '@/features/jobs/details/tax-type/info-gathering/components/CompleteInfoGatheringEarlyConfirmationDialog/CompleteInfoGatheringEarlyConfirmationDialogContainer'
import { useObservable } from '@/hooks/rx-hooks'
import { useDocumentTitle } from '@/hooks/document-title'
import {
  WizardChatIconButtonContainer,
  WizardConversationDialogContainer,
  WizardConversationSidePanelContainer,
  useWizardConversationState,
} from '@/features/messages/components/WizardConversation'
import { useProvideSupportWidgetHidden } from '@/features/support/components/ChatSupportSpaceManager'
import { useLightDarkFn } from '@/features/shell/theming/color-scheme-hooks'
import { usePageExitMethod } from '@/features/shell/exiting/exit-method-hooks'
import { MeasureOneImportDocumentsDialog } from '@/features/jobs/details/tax-type/info-gathering/measure-one/components/MeasureOneImportDocumentsDialog'
import { RelationManagerChatHeader } from '@/features/relation-manager/components/RelationManagerChatHeader'

/**
 * Full-screen info gathering view for tax jobs.
 */
export default observer(function TaxInfoGathering() {
  const isSpacious = useIsSpacious()
  const navigate = useNavigate()

  // The route loader returns the loaded job.
  const { vm } = useLoaderData<{ vm: JobDetailsViewModel }>()
  const taxVm = vm.typeVm as TaxJobDetailsViewModel
  const taxInfoGatheringVm = taxVm.infoGatheringVm
  const pageVm = taxInfoGatheringVm.pageVm
  const relationManager = vm.relationManager

  useActivation(vm)
  useDocumentTitle(vm.job.title)

  // We'll need to activate the conversation VM here as well.
  useActivation(taxVm.jobConversationVm)

  // Info gathering is a full-screen experience, so hide the global nav bar.
  useHideGlobalNavbar(true)

  // When navigating between items, we don't want to display the indicator.
  // Basically, the route loader will always run, but it'll resolve very quickly
  // due to all the async stuff being cached, but that will cause a quick flip
  // of the navigation state and show the indicator flash.
  useDisableGlobalNavigationIndicator(true)

  // Sync URL state with the selected item.
  const { selectedItemId: selectedItemIdParam } = useParams<{
    selectedItemId: string | undefined
  }>()

  useUrlSyncedViewModelState({
    urlValue: selectedItemIdParam ?? null,
    vmValue$: pageVm.selectedItemVm$,
    syncToUrl: (itemVm) =>
      navigate(links.taxJobInfoGathering(vm.job.id, itemVm.item.id), {
        replace: true,
      }),
    applyToVm: (itemId) => {
      const itemVm = itemId
        ? pageVm.infoGatheringVm.getItemVm(itemId)
        : // Fall back to the first incomplete item, or the last item if
          // all are complete.
          (pageVm.allOrderedItems.find((i) => !i.item.isCompleted) ??
          pageVm.allOrderedItems[pageVm.allOrderedItems.length - 1] ??
          null)
      pageVm.selectItem(itemVm)
    },
  })

  const isChatAvailable = taxVm.jobConversationVm.isAvailableInInfoGathering
  const { isChatOpen, setIsChatOpen, openChat, closeChat, isPanelOffside } =
    useWizardConversationState({
      isChatAvailable,
    })

  useProvideSupportWidgetHidden(isChatAvailable)

  // Whenever we detect that the job is no longer in info gathering,
  // redirect to job details.
  useObservable(taxInfoGatheringVm.infoGatheringCompleted$, () => {
    void navigate(
      links.jobDetails(vm.job.id, { showExpectationsDialog: true }),
      { replace: true },
    )
  })

  if (!pageVm.selectedItemVm) {
    // Shouldn't happen; the route loader and hooks ensure
    // we always have an item selected.
    return null
  }

  const relationManagerHeader = relationManager ? (
    <RelationManagerChatHeader
      scheduleCallLink={relationManager.meetingLink}
      displayName={relationManager.fullName}
      avatarUrl={relationManager.avatarUrl}
    />
  ) : undefined

  return (
    <>
      <WizardBottomBarPrimaryButtonContextProvider>
        <Wizard size="lg" testId="tax-info-gathering-view">
          <TopBarContainer
            pageVm={pageVm}
            isChatAvailable={isChatAvailable}
            isChatOpen={isChatOpen}
            taxVm={taxVm}
            onOpenChat={openChat}
            isMeasureOneEnabled={pageVm.infoGatheringVm.isMeasureOneEnabled}
          />
          <WizardPageLayout>
            {isSpacious && (
              <WizardPageStickyColumn stretch>
                <TaxInfoGatheringNavContainer vm={taxInfoGatheringVm} />
              </WizardPageStickyColumn>
            )}
            <WizardPageMainColumn>
              <WizardPageAnimatedContent
                animationKey={pageVm.selectedItemVm.item.id!}
                animationDirection={
                  pageVm.direction === 'forward' ? 'forward' : 'back'
                }
              >
                <HeaderContainer
                  taxInfoGatheringVm={taxInfoGatheringVm}
                  itemVm={pageVm.selectedItemVm}
                />
                <CurrentContentContainer
                  itemVm={pageVm.selectedItemVm}
                  taxInfoGatheringVm={taxInfoGatheringVm}
                />
              </WizardPageAnimatedContent>
            </WizardPageMainColumn>
            {isSpacious && isChatAvailable && (
              <WizardPageStickyColumn>
                <ConversationSidePanelContainer
                  header={relationManagerHeader}
                  taxVm={taxVm}
                  isOffside={isPanelOffside}
                  isOpen={isChatOpen}
                  onOpenChange={setIsChatOpen}
                />
              </WizardPageStickyColumn>
            )}
          </WizardPageLayout>
          <BottomBarContainer taxInfoGatheringVm={taxInfoGatheringVm} />
        </Wizard>

        <TaxInfoGatheringNavigationModalContainer vm={taxInfoGatheringVm} />
        <TaxInfoGatheringDialogsContainer vm={taxVm} />
        <ConversationDialogContainer
          taxVm={taxVm}
          isOpen={isChatOpen && !isSpacious}
          onClose={closeChat}
          header={relationManagerHeader}
        />
        <CompleteInfoGatheringEarlyConfirmationDialogContainer
          vm={taxInfoGatheringVm}
        />
        <TaxInfoGatheringMissingRequiredItemsModalContainer
          vm={taxInfoGatheringVm}
        />
      </WizardBottomBarPrimaryButtonContextProvider>
      <MeasureOneImportDocumentsDialog
        jobId={vm.job.id}
        onClose={taxInfoGatheringVm.measureOneVm.dismissModal}
        isOpen={taxInfoGatheringVm.measureOneVm.measureOneImportModalVm.showing}
      />
    </>
  )
})

interface TopBarContainerProps {
  pageVm: TaxInfoGatheringViewModel['pageVm']
  isChatAvailable: boolean
  isChatOpen: boolean
  taxVm: TaxJobDetailsViewModel
  onOpenChat: () => unknown
  isMeasureOneEnabled: boolean
}

const TopBarContainer = observer(function TopBarContainer({
  pageVm,
  isChatAvailable,
  isChatOpen,
  taxVm,
  onOpenChat,
}: TopBarContainerProps) {
  const isSpacious = useIsSpacious()
  const {
    shell: { shellInfo },
  } = useRootStore()
  const lightDark = useLightDarkFn()
  const { isExitMethodVisible, exitMethodProps } = usePageExitMethod()
  const measureOneVm = taxVm.infoGatheringVm.measureOneVm

  return (
    <WizardTopBar
      title={pageVm.selectedItemVm?.item.heading}
      titleTextAnimationKey={pageVm.selectedItemVm?.item.id}
      left={
        isSpacious ? (
          <WizardTopBarLogo
            src={lightDark(shellInfo.iconUrl, shellInfo.invertedIconUrl)}
            alt={shellInfo.name}
            {...(isExitMethodVisible && exitMethodProps)}
          />
        ) : pageVm.currentStepIndex === 0 ? (
          isExitMethodVisible && (
            <IconButton
              label="Back to home"
              name="back"
              testId="back-to-home-button"
              {...exitMethodProps}
            />
          )
        ) : (
          <IconButton
            label="Previous"
            name="back"
            onPress={pageVm.selectPreviousItem}
            testId="previous-button"
          />
        )
      }
      right={
        isSpacious ? (
          <ButtonGroup spacing="lg">
            <WizardChatIconButtonContainer
              isChatAvailable={isChatAvailable}
              isChatOpen={isChatOpen}
              conversationVm={taxVm.jobConversationVm.conversationVm}
              onOpenPress={onOpenChat}
            />
            {pageVm.infoGatheringVm.isMeasureOneEnabled && (
              <Button
                variant="outline"
                label="Auto-import documents"
                testId="info-gathering-auto-import-documents-button"
                to={measureOneVm.autoImportDocumentsLink}
              />
            )}
            {isExitMethodVisible && (
              <Button
                variant="outline"
                label="Exit"
                iconLeft="close"
                testId="exit-button"
                {...exitMethodProps}
              />
            )}
          </ButtonGroup>
        ) : (
          <ButtonGroup spacing="lg">
            <WizardChatIconButtonContainer
              isChatAvailable={isChatAvailable}
              isChatOpen={isChatOpen}
              conversationVm={taxVm.jobConversationVm.conversationVm}
              onOpenPress={onOpenChat}
            />

            <IconButton
              name="menu"
              label="View menu"
              onPress={pageVm.navigationModalVm.show}
              testId="top-bar-view-menu-button"
            />
          </ButtonGroup>
        )
      }
    />
  )
})

interface HeaderContainerProps {
  taxInfoGatheringVm: TaxInfoGatheringViewModel
  itemVm: SupportingItemViewModel
}

/**
 * Content header container that displays the item's heading and description.
 */
const HeaderContainer = observer(
  function TaxInfoGatheringWizardContentHeaderContainer({
    taxInfoGatheringVm,
    itemVm,
  }: HeaderContainerProps) {
    const currentStepNumber = taxInfoGatheringVm.pageVm.currentStepIndex + 1
    const isEnabledAndTxfJob = taxInfoGatheringVm.txfJobVm.isEnabledAndTxfJob

    return (
      <WizardContentHeader>
        {!isEnabledAndTxfJob && (
          <WizardContentHeaderKicker>
            {currentStepNumber} of {taxInfoGatheringVm.pageVm.counts.total}
          </WizardContentHeaderKicker>
        )}

        <WizardContentHeaderTitle testId="tax-info-gathering-page-title">
          {itemVm.item.heading}
        </WizardContentHeaderTitle>
        <WizardContentHeaderDescription testId="tax-info-gathering-page-description">
          {itemVm.item.longDescription}
        </WizardContentHeaderDescription>
      </WizardContentHeader>
    )
  },
)

interface BottomBarContainerProps {
  taxInfoGatheringVm: TaxInfoGatheringViewModel
}

/**
 * Container component for the bottom bar
 */
const BottomBarContainer = observer(function BottomBarContainer({
  taxInfoGatheringVm,
}: BottomBarContainerProps) {
  const isSpacious = useIsSpacious()
  const pageVm = taxInfoGatheringVm.pageVm
  const isSkeleton = taxInfoGatheringVm.isFetching
  const bottomBarPrimaryButtonProps = useWizardBottomBarPrimaryButton()
  const { selectedItemVm } = taxInfoGatheringVm.pageVm
  const isLastItem = selectedItemVm
    ? taxInfoGatheringVm.pageVm.isLastItem(selectedItemVm)
    : false

  return (
    <WizardBottomBar progress={pageVm.counts.completionPercentage}>
      <WizardBottomBarSection>
        {isSpacious ? (
          <BottomBarCurrentStepLabelContainer
            pageVm={pageVm}
            isSkeleton={isSkeleton}
          />
        ) : (
          <BottomBarExitButtonContainer
            pageVm={pageVm}
            isSkeleton={isSkeleton}
          />
        )}
      </WizardBottomBarSection>

      <WizardBottomBarSpacer />
      <WizardBottomBarSection>
        <ButtonGroup>
          {bottomBarPrimaryButtonProps && (
            <WizardBottomBarPrimaryButton
              {...bottomBarPrimaryButtonProps}
              isAlwaysVisible={isLastItem}
              variant={
                // `text` variant does not look good in the bottom bar,
                // so we'll override it to be `outline`.
                bottomBarPrimaryButtonProps.variant === 'text'
                  ? 'outline'
                  : bottomBarPrimaryButtonProps.variant
              }
            />
          )}
        </ButtonGroup>
      </WizardBottomBarSection>
    </WizardBottomBar>
  )
})

interface BottomBarExitButtonContainerProps {
  pageVm: TaxInfoGatheringViewModel['pageVm']
  isSkeleton: boolean
}

/**
 * Bottom Bar Exit Button Container
 */
const BottomBarExitButtonContainer = observer(
  function BottomBarExitButtonContainer({
    pageVm,
    isSkeleton,
  }: BottomBarExitButtonContainerProps) {
    const { isExitMethodVisible, exitMethodProps } = usePageExitMethod()
    if (isExitMethodVisible) {
      return (
        <Button
          isSkeleton={isSkeleton}
          variant="text"
          label="Exit"
          testId="bottom-bar-exit-button"
          {...exitMethodProps}
        />
      )
    }

    return (
      <BottomBarCurrentStepLabelContainer
        isSkeleton={isSkeleton}
        pageVm={pageVm}
      />
    )
  },
)

interface BottomBarCurrentStepLabelContainerProps {
  pageVm: TaxInfoGatheringViewModel['pageVm']
  isSkeleton: boolean
}

/**
 * Shows the current step information in the bottom bar.
 */
const BottomBarCurrentStepLabelContainer = observer(
  ({ pageVm, isSkeleton }: BottomBarCurrentStepLabelContainerProps) => (
    <WizardBottomBarLabel isSkeleton={isSkeleton}>
      {pageVm.currentStepIndex + 1} of {pageVm.counts.total}
    </WizardBottomBarLabel>
  ),
)

interface ConversationSidePanelContainerProps {
  taxVm: TaxJobDetailsViewModel
  isOffside: boolean
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => unknown
  header?: ReactNode
}

/**
 * Conversation Side Panel Container
 */
const ConversationSidePanelContainer = observer(
  function ConversationSidePanelContainer({
    taxVm,
    isOffside,
    isOpen,
    onOpenChange,
    header,
  }: ConversationSidePanelContainerProps) {
    return (
      <WizardConversationSidePanelContainer
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        positioning={isOffside ? 'offside' : 'floating'}
      >
        <TaxJobConversationContainer taxVm={taxVm} header={header} />
      </WizardConversationSidePanelContainer>
    )
  },
)

interface ConversationDialogContainerProps {
  taxVm: TaxJobDetailsViewModel
  isOpen: boolean
  onClose: () => unknown
  header?: ReactNode
}

/**
 * Conversation Dialog Container
 */
const ConversationDialogContainer = observer(
  function ConversationDialogContainer({
    taxVm,
    isOpen,
    onClose,
    header,
  }: ConversationDialogContainerProps) {
    return (
      <WizardConversationDialogContainer isOpen={isOpen} onClose={onClose}>
        <TaxJobConversationContainer header={header} taxVm={taxVm} />
      </WizardConversationDialogContainer>
    )
  },
)

interface CurrentContentContainerProps {
  taxInfoGatheringVm: TaxInfoGatheringViewModel
  itemVm: SupportingItemViewModel
}

/**
 * Renders the content for the currently selected item.
 */
const CurrentContentContainer = observer(function CurrentContentContainer({
  itemVm,
  taxInfoGatheringVm,
}: CurrentContentContainerProps) {
  switch (itemVm.type) {
    case 'PersonalInfo':
      return (
        <PersonalInfoWizardContentContainer
          itemVm={itemVm}
          taxInfoGatheringVm={taxInfoGatheringVm}
        />
      )

    case 'SpousalInfo':
      return (
        <SpousalInfoWizardContentContainer
          itemVm={itemVm}
          taxInfoGatheringVm={taxInfoGatheringVm}
        />
      )

    case 'DependentsInfo':
      return (
        <DependentsInfoWizardContentContainer
          itemVm={itemVm}
          taxInfoGatheringVm={taxInfoGatheringVm}
        />
      )

    case 'PersonalTaxRefundMethod':
      return (
        <PersonalTaxRefundMethodWizardContentContainer
          itemVm={itemVm}
          taxInfoGatheringVm={taxInfoGatheringVm}
        />
      )

    case 'BusinessTaxRefundMethod':
      return (
        <BusinessTaxRefundMethodWizardContentContainer
          itemVm={itemVm}
          taxInfoGatheringVm={taxInfoGatheringVm}
        />
      )

    case 'BusinessInfo':
      return (
        <BusinessInfoWizardContentContainer
          itemVm={itemVm}
          taxInfoGatheringVm={taxInfoGatheringVm}
        />
      )

    case 'Document':
      return (
        <DocumentWizardContentContainer
          itemVm={itemVm}
          taxInfoGatheringVm={taxInfoGatheringVm}
        />
      )

    case 'Notes':
      return (
        <NotesWizardContentContainer
          itemVm={itemVm}
          taxInfoGatheringVm={taxInfoGatheringVm}
        />
      )

    default:
      return <></>
  }
})
